import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import CommonTable from '../../../components/common-table/CommonTable';
import CustomersTableItem from './CustomersTableItem';
import Tr from '../../../components/common-table/components/Tr';
import Th from '../../../components/common-table/components/Th';
import ThWithSort from '../../../components/common-table/components/ThWithSort';
import ConfirmationModal from '../../../components/confirmation-modal/ConfirmationModal';
import DeleteLabel from '../../../projects/projects-list/components/DeleteLabel';
import CommonPagination from '../../../components/common-pagination/CommonPagination';
import { changeCustomersTableSettings } from '../../../redux/customers/customersReducer';
import { deleteCustomer, getCustomers } from '../../../redux/customers/customersActions';
import calculateSortStr from '../../../utils/calculateSortValue';

import scss from './CustomersTable.module.scss';

const CustomersTable = () => {
  const { t } = useTranslation('customers');
  const dispatch = useDispatch();

  const [selectedCustomer, setSelectedCustomer] = useState({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const isLoading = useSelector((state) => state.customers.isCustomersLoading);
  const customers = useSelector((state) => state.customers.customers);
  const customersTableSettings = useSelector((state) => state.customers.customersTableSettings);
  const { size, page, sort } = customersTableSettings;
  const totalElements = useSelector((state) => state.customers.customersTotal);

  const handleChangeTablePage = useCallback(
    (newPage) => {
      dispatch(changeCustomersTableSettings({ page: newPage }));
    },
    [dispatch],
  );

  const handleChangeSort = useCallback(
    (sortValue) => {
      dispatch(changeCustomersTableSettings({ sort: calculateSortStr({ currentSort: sort, newSortKey: sortValue }) }));
    },
    [dispatch, sort],
  );

  const handleCloseModals = useCallback(() => {
    setIsDeleteModalOpen(false);
  }, []);

  const handleOpenDeleteModal = useCallback((customer) => {
    setSelectedCustomer(customer);
    setIsDeleteModalOpen(true);
  }, []);

  const handleDeleteProject = useCallback(() => {
    dispatch(deleteCustomer({ id: selectedCustomer.id, customerName: selectedCustomer.customerName }));
    setSelectedCustomer({});
  }, [dispatch, selectedCustomer]);

  useEffect(() => {
    dispatch(getCustomers());
  }, [dispatch, customersTableSettings]);

  return (
    <div>
      <CommonTable
        content={customers}
        isLoading={isLoading}
        renderBody={(item) => <CustomersTableItem key={item.id} customer={item} onDelete={handleOpenDeleteModal} />}>
        <Tr>
          <ThWithSort label={t('company')} columnKey='companyName' sortStr={sort} onSort={handleChangeSort} isLeft />
          <Th isLeft>{t('customerId')}</Th>
          <ThWithSort label={t('city')} columnKey='city' sortStr={sort} onSort={handleChangeSort} isLeft />
          <Th extraClasses={scss.cellUsers}>{t('activeUsers')}</Th>
          <Th extraClasses={scss.cellEnable}>{t('enabled')}</Th>
          <Th extraClasses={scss.cellAction} isLeft>
            {t('actions')}
          </Th>
        </Tr>
      </CommonTable>

      <div className={scss.paginationWrapper}>
        <CommonPagination
          activePage={page}
          itemsPerPage={size}
          totalItemsCount={totalElements}
          onChangePage={handleChangeTablePage}
        />
      </div>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseModals}
        onCancel={handleCloseModals}
        onConfirm={handleDeleteProject}
        subLabel={<DeleteLabel name={selectedCustomer.companyName} />}
      />
    </div>
  );
};

export default CustomersTable;
